import { FC, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CaptionMD, ParagraphLG, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { useThemeContext } from '@/contexts/ThemeContext'
import { AngelDialog } from '@/molecules/AngelDialog'
import { SharingBlock } from '@/organisms/ExpressCheckoutCompleteScreen/SharingBlock'
import { createWebSharingUrl } from '@/organisms/SocialSharing/SocialSharingSOF'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { ExpressGradientWrapper } from '@/views/ExpressCheckoutView/ExpressCheckoutComplete/components/ExpressGradientWrapper'

const smsLinks = {
  [slugs.soundOfFreedom]: '5cce56adbff0f65e690875f885e95e53',
  [slugs.afterDeath]: '0c66f15118cce1e22415843af0b50914',
  [slugs.theShift]: 'ed98b6713f57383add2c24362ae9b9ca',
  [slugs.cabrini]: '49bfa03ae2b7a7b5f9e1cf34b569057d',
  [slugs.sight]: '83e6cdcc633f8ec8ede81f222ac16b64',
  [slugs.soundOfHopeTheStoryOfPossumTrot]: '961deb708e9637ef9dd0bdcb3af3f7b2',
  [slugs.bonhoeffer]: '7cc6b88f5c724e80110f95b356d3964e',
  [slugs.homestead]: 'b3da9dbdf03eac89e55cca93226d4198',
}

interface Props {
  projectSlug: string
  projectName: string
}

export const ExpressShare: FC<Props> = ({ projectSlug, projectName }) => {
  const { track } = useSafeAnalytics()
  const { t } = useTranslate('pif')
  const { isDarkMode } = useThemeContext()
  const [isCopied, setIsCopied] = useState(false)
  const [isSmsModalOpen, setSmsModalOpen] = useState<boolean>(false)
  const smsRef = useRef<HTMLIFrameElement>(null)
  const smsLink = smsLinks[projectSlug]

  useEffect(() => {
    if (!isCopied) return
    const copyTimout = setTimeout(() => {
      setIsCopied(false)
    }, 3000)

    return () => {
      clearTimeout(copyTimout)
    }
  }, [isCopied])

  return (
    <>
      {smsLink && (
        <>
          <FlexColumn className="gap-3">
            <TitleMD color={isDarkMode ? 'white' : 'core-gray-950'} weight="semibold">
              {t('becomeAnAngelAmbassadorV3', 'Become an Angel Ambassador')}
            </TitleMD>
            <ParagraphLG weight="normal" color={isDarkMode ? 'white' : 'core-gray-950'}>
              {t(
                'expressCheckoutWithTheWordAmbassadorV3',
                'Share {{projectName}} with the world by signing up to be an Angel Ambassador',
                { projectName },
              )}
            </ParagraphLG>
            <Button
              variant={isDarkMode ? 'white' : 'black'}
              className="mt-1 w-full"
              onClick={() => {
                track('Clicked SMS Signup', { project: projectSlug })
                setSmsModalOpen(true)
              }}
            >
              {t('joinTheMovement', 'Join the Movement')}
            </Button>
          </FlexColumn>
          <AngelDialog
            open={isSmsModalOpen}
            onClose={() => setSmsModalOpen(false)}
            title={t('joinTheMovement', 'Join the Movement')}
            isDarkMode={false}
          >
            <iframe
              ref={smsRef}
              src={`https://widget.smsinfo.io/v2/${smsLink}`}
              width="300"
              height="342"
              frameBorder="0"
            />
          </AngelDialog>
        </>
      )}
      <FlexColumn className="gap-3">
        <TitleMD color={isDarkMode ? 'white' : 'core-gray-950'} weight="semibold">
          {t('shareFreeTicketsWithOthers', 'Share Free Tickets with Others')}
        </TitleMD>
        <ParagraphLG weight="normal" color={isDarkMode ? 'white' : 'core-gray-950'}>
          {t(
            'shareThisLinkWithAnyoneExpressCheckoutV3',
            'Share this link with anyone that wants to watch {{projectName}}, but cannot afford the tickets.',
            { projectName },
          )}
        </ParagraphLG>
        <ExpressGradientWrapper className="mt-1">
          <div
            className={classNames(
              'flex items-center justify-between rounded-md py-3 px-4',
              isDarkMode ? 'bg-core-gray-900 text-core-gray-400' : 'bg-white',
            )}
          >
            <CaptionMD
              className="truncate whitespace-nowrap"
              weight="medium"
              color={isDarkMode ? 'core-gray-400' : 'core-gray-950'}
            >
              {`${paths.getBaseURL()}/tickets/${projectSlug}?promo=claim-free-ticket`}
            </CaptionMD>
            <Button
              className="shrink-0"
              onClick={async () => {
                await navigator.clipboard.writeText(
                  createWebSharingUrl(`${paths.getBaseURL()}/tickets/${projectSlug}?promo=claim-free-ticket`),
                )
                setIsCopied(true)
              }}
              variant={isDarkMode ? 'white' : 'black'}
            >
              {isCopied ? t('copiedUrl', 'Copied URL') : t('copyUrl', 'Copy URL')}
            </Button>
          </div>
        </ExpressGradientWrapper>
        <SharingBlock
          iconColor={isDarkMode ? 'white' : 'black'}
          iconButtonClassName={isDarkMode ? '!bg-core-gray-900 !h-12 !w-12' : '!bg-white !h-12 !w-12'}
          className="mt-4"
          showTitle={false}
          projectSlug={projectSlug}
          hideImpact
        />
      </FlexColumn>
    </>
  )
}
